import React, { FC, SetStateAction, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDebouncedCallback } from 'use-debounce/esm';
import { useRSR } from 'shared/hooks';
import { LinkButton } from 'ui/Buttons';
import { useToasts } from '@scope/scope-one-ui/core/Messages/context';
import { ColumnDefinition } from '@scope/scope-one-ui/core/DataTable/DataTable.d';
import { ActionsMenu, ChipButton, DataTable, StatusBadge } from '@scope/scope-one-ui/core';
import LockIcon from '@scope/scope-one-ui/assets/icons/lock.svg';
import { AddUserRSR } from './Modals/AddUserRSR';
import { SetSubscriberConfirmationModal } from './Modals/SetSubscriberConfirmationModal';
import { AddUserVariants, SubscriberConfirmation, SubscriptionRatingType, User } from './types';
import { deactivateUsers, getUsers } from './services';
import UserStatusIndicator from './components/UserStatusIndicator';
import SubscriberConfirmationIndicator from './components/SubscriberConfirmationIndicator';
import RefreshIcon from '../../../assets/icons/refresh-cw.svg';
import UnlockIcon from '../../../assets/icons/unlock.svg';
import { ConfirmModal } from './Modals/ConfirmModal';

type UsersListProps = {
  ratingType: SubscriptionRatingType;
  isAddUserModalOpen: boolean;
  setIsAddUserModalOpen: React.Dispatch<SetStateAction<boolean>>;
  addUserVariant: AddUserVariants;
  setAddUserVariant: React.Dispatch<SetStateAction<AddUserVariants>>;
};

export const UsersList: FC<UsersListProps> = ({
  ratingType,
  isAddUserModalOpen,
  setIsAddUserModalOpen,
  setAddUserVariant,
  addUserVariant,
}) => {
  const { t } = useTranslation();
  const { error, success } = useToasts();
  const toggleAddUserRSRModal = () => setIsAddUserModalOpen(!isAddUserModalOpen);

  const [isSubscriberConfirmationModalOpen, setSubscriberConfirmationModalOpen] = useState(false);
  const [selectedUser, setSelectedUser] = useState<User>();
  const [isConfirmModalOpen, setIsConfirmModalOpen] = useState(false);

  const editUserHandler = (user: User) => {
    setSelectedUser(user);
    setAddUserVariant(AddUserVariants.PERMISSIONS);
    toggleAddUserRSRModal();
  };

  const handleSubscriberConfirmationClose = (changed: boolean) => {
    setSubscriberConfirmationModalOpen(false);
    if (changed) {
      fetchUsers();
    }
  };

  const showSubscriberConfirmationModal = (user: User) => {
    setSubscriberConfirmationModalOpen(true);
    setSelectedUser(user);
  };

  const userAddedHandler = async (added: boolean) => {
    if (added) {
      fetchUsers();
    }
    toggleAddUserRSRModal();
    setSelectedUser(undefined);
  };

  const nameRender = (user: User) => (
    <LinkButton
      to="#"
      label={`${user.firstName || '-'} ${user.lastName || ''}`.toUpperCase()}
      color="primary"
      onClick={() => {
        editUserHandler(user);
      }}
    />
  );

  const organisationRenderer = (organisationName: string) => organisationName || '-';

  const accesibleRatingsRender = (user: User) => {
    return user.accessibleRatings?.length ? (
      <StatusBadge status="CHECKED" />
    ) : (
      <StatusBadge status="CROSSED" />
    );
  };

  const approvedRatingsRender = (user: User) => (
    <ChipButton
      onClick={() => {
        editUserHandler(user);
      }}
    >
      {user.approvedRatings?.length ?? 0}
    </ChipButton>
  );

  const subscriberConfirmationRender = (confirmation: SubscriberConfirmation) => {
    return confirmation ? <SubscriberConfirmationIndicator status={confirmation} /> : '-';
  };

  const statusRender = (userStatus) => {
    return <UserStatusIndicator status={userStatus} />;
  };

  const getActions = (item: User) => [
    {
      label: t('ADMIN.USERS.EDIT_USER'),
      onClick: () => editUserHandler(item),
      icon: <UnlockIcon />,
    },
    {
      label: t('ADMIN.RSR.SET_SUBSCRIBER_CONFIRMATION'),
      onClick: () => showSubscriberConfirmationModal(item),
      icon: <RefreshIcon />,
    },
    {
      label: t('ADMIN.USERS.REVOKE'),
      onClick: () => {
        setSelectedIds([item.userId]);
        setIsConfirmModalOpen(true);
      },
      icon: <LockIcon />,
    },
  ];

  const initialColumns: ColumnDefinition[] = [
    {
      name: 'name',
      label: t('ADMIN.RSR.NAME'),
      sortable: true,
      customBodyRender: nameRender,
      searchable: true,
    },
    {
      name: 'organisationName',
      label: t('ADMIN.RSR.ORGANISATION'),
      sortable: true,
      customBodyRender: organisationRenderer,
    },
    {
      name: 'confirmation',
      label: t('ADMIN.RSR.SUBSCRIBER_CONFIRMATION'),
      sortable: true,
      customBodyRender: subscriberConfirmationRender,
    },
    {
      name: 'email',
      label: t('ADMIN.RSR.EMAIL'),
      sortable: true,
    },
    {
      name: 'userStatus',
      label: t('ADMIN.RSR.USER_STATUS'),
      sortable: true,
      customBodyRender: statusRender,
    },
    {
      name: 'access',
      label: t('ADMIN.RSR.ACCESS'),
      customBodyRender: accesibleRatingsRender,
    },
    {
      name: 'approvedRatings',
      label: t('ADMIN.RSR.APPROVED_RATINGS'),
      customBodyRender: approvedRatingsRender,
    },
    {
      name: 'moreActions',
      label: ' ',
      customBodyRender: (user) => <ActionsMenu list={getActions(user)} />,
    },
  ];
  const buildRows = (data: User[]) =>
    data.map((user) => {
      return {
        id: user.userId,
        data: {
          name: user,
          organisationName: user.organisationName,
          confirmation: user.confirmation,
          email: user.email,
          userStatus: user.userStatus,
          access: user,
          approvedRatings: user,
          moreActions: user,
        },
      };
    });

  const {
    tableActions,
    selectToggle,
    selectedIds,
    searchedValue,
    sort,
    rows,
    loading,
    setLoading,
    pagination,
    setRows,
    selectAllToggle,
    isSelectedAll,
    setSelectedIds,
    setTotal,
  } = useRSR({ initialColumns, defaultSortField: 'name' });
  const fetchUsers = () => {
    setLoading(true);
    getUsers(
      ratingType,
      pagination.page || 1,
      pagination.limit,
      {
        sort: sort.field,
        dir: sort.order,
      },
      searchedValue,
    )
      .then((response) => {
        setRows(buildRows(response.data));
        setTotal(response.total);
      })
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    fetchUsers();
  }, [pagination, sort]);

  const fetchDebounce = useDebouncedCallback(fetchUsers, 300);

  useEffect(() => {
    fetchDebounce();
  }, [searchedValue]);

  const revoke = () => {
    deactivateUsers(ratingType, selectedIds)
      .then(() => {
        success({ title: '', content: t('ADMIN.SNACKS.USERS_REVOKED') });
        fetchUsers();
      })
      .catch(() => error({ title: '', content: t('ADMIN.SNACKS.USERS_REVOKE_FAILED') }));
  };

  const headerActions = [
    {
      icon: <LockIcon />,
      label: 'REVOKE ALL RATINGS',
      onClick: () => {
        setIsConfirmModalOpen(true);
      },
    },
  ];

  return (
    <>
      <DataTable
        selection={{
          selectedIds,
          selectToggle,
          selectAllToggle,
          isSelectedAll,
        }}
        headerActions={headerActions}
        loading={loading}
        title="Users"
        cols={initialColumns}
        rows={rows}
        selectable
        paginate
        tableActions={tableActions}
      />
      <AddUserRSR
        ratingType={ratingType}
        variant={addUserVariant}
        open={isAddUserModalOpen}
        onClose={userAddedHandler}
        user={selectedUser}
      />
      <SetSubscriberConfirmationModal
        open={isSubscriberConfirmationModalOpen}
        onClose={handleSubscriberConfirmationClose}
        user={selectedUser}
      />
      <ConfirmModal
        title="Revoke ratings"
        variant="warning"
        text="Do you want to revoke all ratings for selected users?"
        confirmLabel="Revoke all ratings"
        onClose={() => setIsConfirmModalOpen(false)}
        onConfirm={() => {
          revoke();
          setIsConfirmModalOpen(false);
          setSelectedIds([]);
        }}
        open={isConfirmModalOpen}
      />
    </>
  );
};
